/* eslint-disable no-console */
/**
 * A simple pub/sub helper for asynchronus ajax requests made by the
 * lightspeed frontend.
 *
 * Usage: Subscribe to ajax request for the form "item.listing"
 *
 * asyncSubscribe.subscribeToFormCall("item.listing", callback);
 *
 * Once subscribed,  whenver a call to the "item.listing" form is started,  the
 * started callback will be called with the data of the call and the promise
 * object associated with it.  The calback can then assign success/failure/then
 * handlers to the returned promise.
 *
 * Copyright 2015 Lightspeed Retail.  All rights reserved.
 */
class AsyncSubscribe implements MerchantosAsyncSubscribe {
  subscribers: MerchantosAsyncSubscribe['subscribers'];

  subCounter: MerchantosAsyncSubscribe['subCounter'];

  pendingSubscribers: MerchantosAsyncSubscribe['pendingSubscribers'];

  constructor() {
    this.subscribers = {};
    this.subCounter = 0;
    this.pendingSubscribers = {};
  }

  /**
   * Removes a subscriber to a form under the key of the provided subID
   *
   * @param {string} formName - The form name subscription
   * @param {number} subID - Key of the subscriber to formName to be removed.
   */
  private removeSubscription = (formName: string, subID: number) => {
    return delete this.subscribers[formName][subID];
  };

  /**
   * Nearly all async server calls include a form_name, this allows another
   * entity to subscribe to a call to a given formName and be notified
   * whenever a call to that formName has been resolved.
   *
   * @param formName {String}          - The name of the form
   * @param callback {Function} 		- Called when an ajax request for the
   *			provided formName starts.  This cb will receive the data of
   *			the call and the promise object associated with it as
   *			arguments.
   *
   * @return {object}   - An object with an unsubscribe() method the subscriber
   *   		can use to unsubscribe from notifications.
   */
  subscribeToFormCall: MerchantosAsyncSubscribe['subscribeToFormCall'] = (formName, callback) => {
    if (this.pendingSubscribers[formName]) {
      callback(this.pendingSubscribers[formName]);
      delete this.pendingSubscribers[formName];
    }
    // If this form doesn't have any subscribers,  initialize it
    // to an empty object.
    if (_.isUndefined(this.subscribers[formName])) {
      this.subscribers[formName] = {};
    }
    const subID = this.subCounter;
    if (_.isFunction(callback)) {
      // Increment subscriberID;
      this.subscribers[formName][subID] = callback;
    } else {
      console.log('Invalid Callbacks: Must provide callback function for subscription.');
    }

    const unsubscribeHandler = {
      unsubscribe: () => {
        return this.removeSubscription(formName, subID);
      },
    };

    this.subCounter += 1;
    return unsubscribeHandler;
  };

  /**
   * Notifies subscribers registered for a given formName, passing
   * in the extra data about the request and the promise that will be
   * resolved or rejected.
   *
   * @param {string} formName - The form name being called.
   * @param {object} data - The data sent to the form.
   * @param {JQuery.Promise} promise - The promise object of the request.
   */
  notifySubscribers: MerchantosAsyncSubscribe['notifySubscribers'] = (formName, data, promise) => {
    if (formName) {
      if (!_.isUndefined(this.subscribers[formName])) {
        const notifyKeys = Object.keys(this.subscribers[formName]);

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < notifyKeys.length; i++) {
          this.subscribers[formName][notifyKeys[i]](data, promise);
        }
      } else {
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{ [key: string]: any; } | undefined' is not ... Remove this comment to see the full error message
        this.pendingSubscribers[formName] = data;
      }
    }
  };
}

window.merchantos.asyncSubscribe = new AsyncSubscribe();
